import { Badge, Banner, BlockStack, Box, Button, Card, Divider, FormLayout, InlineStack, Select, Text, TextField } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactSelect from 'react-select';
import { useAuthStore, useLookupStore } from '../../../context/useStore';
import { approvalsApi, DOC_TYPE_META } from '../../../shared/components/approvals/approvalsApi';
import PurchaseOrderPanel from '../../../shared/components/approvals/PurchaseOrderPanel';
import CompanySelect from '../../../shared/components/companySelect';
import ProjectSelect from '../../../shared/components/projectSelect';
import { alphabetize, formatToCurrency, getUserOrg } from '../../../shared/components/functions';
import ActionBar from './ActionBar';
import ApprovalTimeline from './ApprovalTimeline';
import ApprovalWorkspace from './ApprovalWorkspace';
import DocumentHeader from './DocumentHeader';
import SupplierHistoryCard from './SupplierHistoryCard';

const dateOnly = (value) => (value ? String(value).substring(0, 10) : '');

function initialForm(card) {
  const doc = card.document;
  return {
    LeverancierNummer: doc.LeverancierNummer || null,
    faktuurref: doc.faktuurref || '',
    datum: dateOnly(doc.datum),
    datumin: dateOnly(doc.datumin),
    datumbetaling: dateOnly(doc.datumbetaling),
    Bedragfaktuur: doc.Bedragfaktuur ?? '',
    BedragExclBtw: doc.BedragExclBtw ?? '',
    ValutaID: doc.ValutaID || 19,
    betalingID: doc.betalingID || '',
    BusinessUnitID: doc.BusinessUnitID || card.suggestions?.prefill?.businessUnitId || null,
    memo: doc.memo || ''
  };
}

const Section = ({ title, children }) => (
  <Box paddingBlockStart="300">
    <BlockStack gap="200">
      <Text variant="headingSm" as="h3">
        {title}
      </Text>
      {children}
    </BlockStack>
  </Box>
);

/*
  Accounting's intake card: verify / complete the document fields (with the extracted values as one-click chips),
  link the purchase order, pick the approver (pre-filled from the PO owner or the supplier history) and send it.
  "Approve myself" is for general costs accounting approves directly.
*/
export default function IntakeCard({ card, setCard, deck, registerActions, pdfCache, onDecided, setToast, setErrorToast }) {
  const { user } = useAuthStore();
  const users = useLookupStore((state) => state.users);
  const organisations = useLookupStore((state) => state.organisations);
  const currencies = useLookupStore((state) => state.currencies);
  const paymentconditions = useLookupStore((state) => state.paymentconditions);
  const purchasetypes = useLookupStore((state) => state.purchasetypes);
  const businessunits = useLookupStore((state) => state.businessunits);
  const meta = DOC_TYPE_META[card.docType];
  const docId = card.document[meta.pk];
  const suggestions = card.suggestions || {};
  const extracted = suggestions.extracted || {};

  const [form, setForm] = useState(() => initialForm(card));
  const [dirty, setDirty] = useState(false);
  const [assigneeId, setAssigneeId] = useState(suggestions.suggestedAssigneeId || card.document.AssigneeID || null);
  const [note, setNote] = useState('');
  const [allocation, setAllocation] = useState({ FactuurTypeID: suggestions.prefill?.costTypeId || card.document.BestellingTypeID || null, ProjectID: suggestions.prefill?.projectId || null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);

  useEffect(() => {
    setForm(initialForm(card));
    setDirty(false);
    setAssigneeId(suggestions.suggestedAssigneeId || card.document.AssigneeID || null);
    setAllocation({ FactuurTypeID: suggestions.prefill?.costTypeId || card.document.BestellingTypeID || null, ProjectID: suggestions.prefill?.projectId || null });
    setNote('');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, card.document.ApprovalStatusUpdatedAt, card.intake?.generatedAt]);

  const setField = (key, value) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    setDirty(true);
  };

  const userOrg = user && getUserOrg(organisations, user.ORGANISATION_NAME);
  const userOptions = useMemo(
    () =>
      users
        .filter((u) => !u.Inactief && u.UserType !== 7 && String(u.Afkorting || '').toUpperCase() !== 'SYS' && (!userOrg || u.Vennootschap === userOrg) && u.Email)
        .sort((a, b) => alphabetize(a, b, 'Afkorting'))
        .map((u) => ({ value: u.GebruikerID, label: `${u.Afkorting} · ${u.Gebruikernaam}` })),
    [users, userOrg]
  );
  const assigneeOption = userOptions.find((o) => o.value === assigneeId) || null;

  const missing = [];
  if (!form.LeverancierNummer) missing.push('supplier');
  if (!String(form.faktuurref).trim()) missing.push('reference');
  if (form.Bedragfaktuur === '' || form.Bedragfaktuur === null) missing.push('total amount');
  if (!form.datum) missing.push('document date');
  if (!form.datumbetaling) missing.push('due date');
  if (meta.hasCostType && !allocation.FactuurTypeID) missing.push('cost type');
  const formReady = missing.length === 0;

  const fieldsPayload = () => ({
    ...form,
    Bedragfaktuur: form.Bedragfaktuur === '' ? null : Number(String(form.Bedragfaktuur).replace(',', '.')),
    BedragExclBtw: form.BedragExclBtw === '' ? null : Number(String(form.BedragExclBtw).replace(',', '.')),
    betalingID: form.betalingID || null,
    selectedS3Key: selectedKey || undefined
  });

  const run = useCallback(
    async (action, { leaves = false } = {}) => {
      setBusy(true);
      setError(null);
      const response = await action();
      setBusy(false);
      if (!response.ok) {
        setError(response.error || 'Something went wrong');
        if (setErrorToast) setErrorToast(true);
        return response;
      }
      if (setToast) setToast(true);
      if (leaves) {
        if (onDecided) onDecided(response.data);
        deck.removeCurrent();
      } else if (response.data?.document && response.data?.lines) {
        setCard(response.data);
      }
      return response;
    },
    [deck, onDecided, setCard, setErrorToast, setToast]
  );

  // save fields + system document (only when something changed or the document was never processed), then assign
  const processThenAssign = useCallback(
    async ({ approveNow = false } = {}) => {
      if (!formReady || busy) return;
      setBusy(true);
      setError(null);
      if (dirty || !card.document.processed) {
        const processed = await approvalsApi.process(card.docType, docId, fieldsPayload());
        if (!processed.ok) {
          setBusy(false);
          setError(processed.error);
          if (setErrorToast) setErrorToast(true);
          return;
        }
      }
      const basis = form.BedragExclBtw !== '' && form.BedragExclBtw !== null ? Number(String(form.BedragExclBtw).replace(',', '.')) : Number(String(form.Bedragfaktuur).replace(',', '.'));
      const lines = [{ Bedrag: basis, FactuurTypeID: allocation.FactuurTypeID || undefined, ProjectID: allocation.ProjectID || null, BusinessUnitID: form.BusinessUnitID || null }];
      const assigned = await approvalsApi.assign(card.docType, docId, { assigneeId: approveNow ? user.id : assigneeId, lines, note: note.trim() || null, approveNow });
      setBusy(false);
      if (!assigned.ok) {
        setError(assigned.error);
        if (setErrorToast) setErrorToast(true);
        return;
      }
      if (setToast) setToast(true);
      if (onDecided) onDecided(assigned.data);
      deck.removeCurrent();
    },
    [allocation, assigneeId, busy, card, deck, dirty, docId, form, formReady, note, onDecided, setErrorToast, setToast, user]
  );

  const saveOnly = () => run(() => approvalsApi.process(card.docType, docId, fieldsPayload()).then((response) => (response.ok ? approvalsApi.card(card.docType, docId) : response)));

  const remove = async () => {
    if (!window.confirm(`Delete ${meta.labelLower} ${card.document[meta.numberField]}? This removes the document and its attachments from the admin.`)) return;
    await run(() => approvalsApi.deleteDocument(card.docType, docId), { leaves: true });
  };

  useEffect(() => {
    if (registerActions) registerActions({ approve: () => processThenAssign(), reject: () => {}, skip: deck.skip });
  }, [processThenAssign, deck.skip, registerActions]);

  const isRejected = card.document.ApprovalStatus === 'REJECTED';
  const actions = [
    { id: 'deleteDocument', content: 'Delete (junk)', tone: 'critical', onAction: remove, disabled: busy },
    ...(dirty ? [{ id: 'saveFields', content: 'Save fields', onAction: saveOnly, disabled: busy || !formReady, loading: busy }] : []),
    { id: 'skipAction', content: 'Skip', onAction: deck.skip, disabled: busy || deck.total < 2, shortcut: 'S' },
    ...(isRejected ? [{ id: 'reopenAction', content: 'Reopen for approval', onAction: () => run(() => approvalsApi.reopen(card.docType, docId, {}), { leaves: true }), disabled: busy }] : []),
    {
      id: 'approveMyself',
      content: 'Approve myself',
      onAction: () => processThenAssign({ approveNow: true }),
      disabled: busy || !formReady,
      disabledReason: missing.length ? `Missing: ${missing.join(', ')}` : null
    },
    {
      id: 'confirmSend',
      content: isRejected ? 'Re-assign & send' : 'Confirm & send for approval',
      variant: 'primary',
      onAction: () => processThenAssign(),
      disabled: busy || !formReady || !assigneeId,
      disabledReason: !assigneeId ? 'Pick an approver' : missing.length ? `Missing: ${missing.join(', ')}` : null,
      loading: busy,
      shortcut: 'A'
    }
  ];

  const chip = (label, onApply, applied) => (
    <Button key={label} size="slim" onClick={onApply} disabled={applied || busy} pressed={applied}>
      {label}
    </Button>
  );

  const costTypeOptions = [
    { label: 'Cost type…', value: '' },
    ...[...purchasetypes].sort((a, b) => alphabetize(a, b, 'BestellingType')).map((pt) => ({ label: pt.BestellingType, value: String(pt.BestellingTypeID) }))
  ];
  const businessUnitOptions = [
    { label: 'No business unit', value: '' },
    ...businessunits.filter((bu) => bu.IsActive).map((bu) => ({ label: `${bu.Code} (${bu.Name})`, value: String(bu.BusinessUnitID) }))
  ];
  const rejectedLines = card.lines.filter((line) => line.Status === 'REJECTED');

  return (
    <ApprovalWorkspace
      resource={meta.apiResource}
      s3Keys={card.s3Keys}
      selectedKey={selectedKey}
      onSelectKey={setSelectedKey}
      pdfCache={pdfCache}
      footer={<ActionBar actions={actions} hint={missing.length ? `Missing: ${missing.join(', ')}` : dirty ? 'Field changes are saved when you send or save' : ''} />}
    >
      <Card>
        <BlockStack gap="300">
          <DocumentHeader card={card} showAssignment={false} />
          {isRejected ? (
            <Banner tone="critical" title="Rejected by the approver">
              {rejectedLines.map((line) => (
                <Text as="p" key={line.id}>
                  {line.approverAfkorting}: <strong>{line.RejectReason}</strong>
                </Text>
              ))}
              <Text as="p">Correct the document (or ask the supplier for a credit note), then reopen it for the same approver or re-assign it.</Text>
            </Banner>
          ) : null}
          {error ? (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          ) : null}
          {card.intake?.textSource ? (
            <InlineStack gap="200" blockAlign="center" wrap>
              <Badge tone="info">{card.intake.textSource === 'PEPPOL' ? 'Peppol data' : card.intake.textSource === 'AI_OCR' ? 'Read by AI (scan)' : 'PDF text read'}</Badge>
              {extracted.confidence ? <Text variant="bodySm" tone="subdued">{`confidence ${extracted.confidence}`}</Text> : null}
              <Button size="slim" variant="plain" onClick={() => run(() => approvalsApi.enrich(card.docType, docId))} disabled={busy}>
                Re-analyse
              </Button>
            </InlineStack>
          ) : null}

          <Divider />
          <Section title="Document">
            {!form.LeverancierNummer && suggestions.supplierCandidates?.length ? (
              <InlineStack gap="200" blockAlign="center" wrap>
                <Text variant="bodySm" tone="subdued">
                  Supplier suggestions{extracted.vatNumber ? ` (VAT ${extracted.vatNumber})` : ''}:
                </Text>
                {suggestions.supplierCandidates.map((candidate) => chip(`${candidate.NAAM} · ${Math.round(candidate.score * 100)}%`, () => setField('LeverancierNummer', candidate.FIRNR), false))}
              </InlineStack>
            ) : null}
            <FormLayout>
              <FormLayout.Group>
                <CompanySelect id="LeverancierNummer" label="Supplier *" value={form.LeverancierNummer} onChange={(value) => setField('LeverancierNummer', value)} isDisabled={busy} />
                <TextField
                  id="faktuurref"
                  label="Supplier reference *"
                  value={form.faktuurref}
                  onChange={(value) => setField('faktuurref', value)}
                  autoComplete="off"
                  maxLength={50}
                  connectedRight={
                    extracted.invoiceNumber && extracted.invoiceNumber !== form.faktuurref ? (
                      <Button onClick={() => setField('faktuurref', extracted.invoiceNumber)}>{`Use ${extracted.invoiceNumber}`}</Button>
                    ) : undefined
                  }
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <TextField
                  id="Bedragfaktuur"
                  label="Total amount *"
                  type="number"
                  prefix="€"
                  value={String(form.Bedragfaktuur ?? '')}
                  onChange={(value) => setField('Bedragfaktuur', value)}
                  autoComplete="off"
                  connectedRight={
                    extracted.totalInclVat != null && Number(extracted.totalInclVat) !== Number(form.Bedragfaktuur) ? (
                      <Button onClick={() => setField('Bedragfaktuur', extracted.totalInclVat)}>{`Use ${formatToCurrency(extracted.totalInclVat)}`}</Button>
                    ) : undefined
                  }
                />
                <TextField
                  id="BedragExclBtw"
                  label="Amount excl. VAT"
                  type="number"
                  prefix="€"
                  value={String(form.BedragExclBtw ?? '')}
                  onChange={(value) => setField('BedragExclBtw', value)}
                  autoComplete="off"
                  helpText={card.document.PeppolStatus ? 'From the Peppol document' : 'Leave empty when the total carries no VAT'}
                  connectedRight={
                    extracted.totalExclVat != null && Number(extracted.totalExclVat) !== Number(form.BedragExclBtw) ? (
                      <Button onClick={() => setField('BedragExclBtw', extracted.totalExclVat)}>{`Use ${formatToCurrency(extracted.totalExclVat)}`}</Button>
                    ) : undefined
                  }
                />
              </FormLayout.Group>
              <FormLayout.Group condensed>
                <TextField
                  id="datum"
                  label="Document date *"
                  type="date"
                  value={form.datum}
                  onChange={(value) => setField('datum', value)}
                  autoComplete="off"
                  connectedRight={extracted.issueDate && extracted.issueDate !== form.datum ? <Button onClick={() => setField('datum', extracted.issueDate)}>Use</Button> : undefined}
                />
                <TextField
                  id="datumbetaling"
                  label="Due date *"
                  type="date"
                  value={form.datumbetaling}
                  onChange={(value) => setField('datumbetaling', value)}
                  autoComplete="off"
                  connectedRight={extracted.dueDate && extracted.dueDate !== form.datumbetaling ? <Button onClick={() => setField('datumbetaling', extracted.dueDate)}>Use</Button> : undefined}
                />
                <TextField id="datumin" label="Received" type="date" value={form.datumin} onChange={(value) => setField('datumin', value)} autoComplete="off" />
              </FormLayout.Group>
              <FormLayout.Group condensed>
                <Select
                  label="Currency"
                  options={currencies.map((c) => ({ label: c.Valuta, value: String(c.ValutaID) }))}
                  value={String(form.ValutaID || '')}
                  onChange={(value) => setField('ValutaID', Number(value))}
                />
                <Select
                  label="Payment condition"
                  options={[
                    { label: 'Select…', value: '' },
                    ...[...paymentconditions].sort((a, b) => alphabetize(a, b, 'betalingNL')).map((c) => ({ label: c.betalingNL, value: String(c.betalingID) }))
                  ]}
                  value={String(form.betalingID || '')}
                  onChange={(value) => setField('betalingID', value ? Number(value) : '')}
                />
                <Select
                  label="Business unit"
                  options={businessUnitOptions}
                  value={form.BusinessUnitID ? String(form.BusinessUnitID) : ''}
                  onChange={(value) => setField('BusinessUnitID', value ? Number(value) : null)}
                />
              </FormLayout.Group>
              <TextField
                id="memo"
                label="Note / content"
                value={form.memo}
                onChange={(value) => setField('memo', value)}
                multiline={2}
                autoComplete="off"
                connectedRight={extracted.summary && !form.memo ? <Button onClick={() => setField('memo', extracted.summary)}>Use summary</Button> : undefined}
              />
            </FormLayout>
          </Section>

          {meta.hasPurchaseOrders ? (
            <Section
              title={
                card.purchaseOrders.length
                  ? `Purchase order${card.purchaseOrders.length > 1 ? 's' : ''} (linked${card.suggestions?.purchaseOrders?.autoLinked?.length ? ' automatically' : ''})`
                  : 'Purchase order'
              }
            >
              <PurchaseOrderPanel
                card={card}
                canEdit
                busy={busy}
                onLink={(bestellingId) => run(() => approvalsApi.linkPurchaseOrder(docId, bestellingId))}
                onUnlink={(bestellingId) => run(() => approvalsApi.unlinkPurchaseOrder(docId, bestellingId))}
              />
            </Section>
          ) : null}

          <Section title="Approval">
            <BlockStack gap="200">
              {suggestions.assignees?.length ? (
                <InlineStack gap="200" blockAlign="center" wrap>
                  <Text variant="bodySm" tone="subdued">
                    Suggested approver:
                  </Text>
                  {suggestions.assignees.slice(0, 4).map((s) => chip(`${s.Afkorting || s.userId} · ${Math.round((s.score || 0) * 100)}%`, () => setAssigneeId(s.userId), assigneeId === s.userId))}
                </InlineStack>
              ) : (
                <Text variant="bodySm" tone="subdued">
                  No suggestion: no purchase order matched and no approval history for this supplier.
                </Text>
              )}
              {assigneeId && suggestions.assignees?.find((s) => s.userId === assigneeId)?.reasons?.length ? (
                <Text variant="bodySm" tone="subdued">
                  {suggestions.assignees.find((s) => s.userId === assigneeId).reasons.join(' · ')}
                </Text>
              ) : null}
              <FormLayout>
                <FormLayout.Group>
                  <div>
                    <Text variant="bodySm">Approver *</Text>
                    <ReactSelect
                      options={userOptions}
                      value={assigneeOption}
                      onChange={(option) => setAssigneeId(option ? option.value : null)}
                      placeholder="Search a colleague…"
                      isClearable
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  </div>
                  {meta.hasCostType ? (
                    <Select
                      id="selectType"
                      label="Cost type *"
                      options={costTypeOptions}
                      value={allocation.FactuurTypeID ? String(allocation.FactuurTypeID) : ''}
                      onChange={(value) => setAllocation((a) => ({ ...a, FactuurTypeID: value ? Number(value) : null }))}
                    />
                  ) : null}
                </FormLayout.Group>
                <FormLayout.Group>
                  <ProjectSelect
                    id="ProjectID"
                    label="Project (prefilled from the purchase order)"
                    value={allocation.ProjectID}
                    onChange={(value) => setAllocation((a) => ({ ...a, ProjectID: value || null }))}
                    index="intake"
                  />
                  <TextField label="Note for the approver" value={note} onChange={setNote} autoComplete="off" maxLength={500} />
                </FormLayout.Group>
              </FormLayout>
            </BlockStack>
          </Section>

          <Section title="Previous invoices of this supplier">
            <SupplierHistoryCard history={card.supplierHistory} />
          </Section>
          <Section title="History">
            <ApprovalTimeline events={card.events} />
          </Section>
        </BlockStack>
      </Card>
    </ApprovalWorkspace>
  );
}
